import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";

const ContactMe = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null); // Add this line

  const captchaRef = useRef();
  // Replace with your actual keys for production
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LfY0aErAAAAAJEzXbJZYDDUGMiBv-lN5OT8Si8L";
  
  // Also update EmailJS variables
  const emailJSServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_utniq68";
  const emailJSTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_9ognx2p";
  const emailJSPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "MeFmAAlgFtp7Y-BNj";

  // Debug info
  useEffect(() => {
    console.log('Site Key:', recaptchaSiteKey ? 'Present' : 'Missing');
    console.log('Current URL:', window.location.href);
    console.log('Environment:', process.env.NODE_ENV);
  }, [recaptchaSiteKey]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaptchaChange = (value) => {
    console.log("Captcha value:", value);
    setCaptchaVerified(!!value);
    setCaptchaToken(value); // Store the actual token
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setStatus({ type: "error", message: "All fields are required!" });
      autoClearStatus();
      return;
    }

    // Skip captcha verification if no site key
    if (recaptchaSiteKey && !captchaVerified) {
      setStatus({ type: "error", message: "Please verify the reCAPTCHA!" });
      autoClearStatus();
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    emailjs
      .send(
        emailJSServiceId,
        emailJSTemplateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          'g-recaptcha-response': captchaToken, // Add the reCAPTCHA token
        },
        emailJSPublicKey
      )
      .then(
        () => {
          setStatus({ type: "success", message: "Message sent successfully!" });
          setFormData({ name: "", email: "", message: "" });
          setCaptchaVerified(false);
          setCaptchaToken(null); // Reset token
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
          setIsSubmitting(false);
          autoClearStatus();
        },
        (error) => {
          console.error("EmailJS error:", error);
          setStatus({
            type: "error",
            message: "Failed to send message. Please try again.",
          });
          setIsSubmitting(false);
          autoClearStatus();
        }
      );
  };

  const autoClearStatus = () => {
    setTimeout(() => {
      setStatus(null);
    }, 5000);
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-6 md:px-12 pt-32 pb-20 bg-gray-100">
      {/* Background Accents */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="w-96 h-96 bg-yellow-200 opacity-20 rounded-full absolute top-10 left-10 transform -translate-x-1/3 -translate-y-1/3"></div>
        <div className="w-72 h-72 bg-yellow-100 opacity-20 rounded-full absolute bottom-10 right-10 transform translate-x-1/3 translate-y-1/3"></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 text-center">
        <h1 className="text-5xl font-extrabold text-gray-900 leading-tight">
          Let's <span className="text-yellow-500">Connect</span>
        </h1>
        <p className="text-lg text-gray-700 mt-4">
          Have a question, project, or just want to say hi? Drop me a message!
        </p>
        <div className="w-16 h-1 bg-yellow-500 mx-auto mt-2 rounded-full"></div>
      </header>

      {/* Contact Form */}
      <div className="relative z-10 bg-white p-8 mt-10 rounded-3xl shadow-md max-w-2xl w-full">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-700 font-medium">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-yellow-500 focus:outline-none"
              placeholder="Enter your name"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-yellow-500 focus:outline-none"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium">Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-yellow-500 focus:outline-none"
              placeholder="Write your message here..."
              rows="5"
            ></textarea>
          </div>

          {/* Simple reCAPTCHA - only show if site key exists */}
          {recaptchaSiteKey && (
            <div className="flex justify-center">
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                onChange={handleCaptchaChange}
                ref={captchaRef}
              />
            </div>
          )}

          {/* Status Message */}
          {status && (
            <p
              className={`text-center font-semibold transition-opacity duration-300 ${
                status.type === "success" ? "text-green-600" : "text-red-600"
              }`}
            >
              {status.message}
            </p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-yellow-500 text-white py-3 rounded-lg font-semibold text-lg shadow-md transform hover:scale-105 transition duration-300 hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>

    </div>
  );
};

export default ContactMe;